import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmailPreviewFrame } from "./EmailPreviewFrame";

const TONE = {
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200", ACCEPTED: "bg-sky-50 text-sky-700 border-sky-200", RETRY_SCHEDULED: "bg-amber-50 text-amber-700 border-amber-200",
  DELAYED: "bg-amber-50 text-amber-700 border-amber-200", BOUNCED: "bg-rose-50 text-rose-700 border-rose-200", COMPLAINED: "bg-rose-50 text-rose-700 border-rose-200",
  FAILED: "bg-rose-50 text-rose-700 border-rose-200", SUPPRESSED: "bg-slate-100 text-slate-600 border-slate-200", DUPLICATE: "bg-slate-100 text-slate-600 border-slate-200",
};
export const OutcomeBadge = ({ outcome, help }) => (
  <span title={help} className={`rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${TONE[outcome] || "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid="outcome-badge">{(outcome || "").replace(/_/g, " ")}</span>
);

const ReasonAction = ({ label, onConfirm, testid }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!open) return <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setOpen(true)} data-testid={testid}>{label}</Button>;
  return (
    <span className="inline-flex items-center gap-1">
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (required)" className="h-6 w-56 text-[11px]" data-testid={`${testid}-reason`} />
      <Button size="sm" className="h-6 text-[11px]" onClick={() => { onConfirm(reason); setOpen(false); setReason(""); }} data-testid={`${testid}-confirm`}>Confirm</Button>
      <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setOpen(false)}>Cancel</Button>
    </span>
  );
};

export const DeliveryLogPanel = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [sups, setSups] = useState([]);
  const load = () => {
    api.get("/comms/delivery", { params: { outcome: filter || undefined, q: q || undefined } }).then(({ data }) => setData(data)).catch(() => {});
    api.get("/comms/suppressions").then(({ data }) => setSups(data.items || [])).catch(() => {});
  };
  useEffect(load, [filter, q]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="text-sm text-slate-400 p-4">Loading delivery log…</div>;

  const release = async (email, reason) => {
    try { await api.post(`/comms/suppressions/${encodeURIComponent(email)}/release`, { reason }); toast.success("Suppression released"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Release failed"); }
  };
  const resend = async (id, reason) => {
    try { const { data } = await api.post(`/comms/delivery/${id}/resend`, { reason }); toast[data.outcome === "ACCEPTED" ? "success" : "error"](`${data.outcome}${data.error ? " — " + data.error : ""}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  };

  return (
    <div className="space-y-3" data-testid="delivery-log-panel">
      <div className="text-[11px] text-slate-500 border border-slate-200 rounded-md px-3 py-2 bg-white flex flex-wrap gap-x-4 gap-y-1" data-testid="delivery-note">
        <span>{data.note}</span>
        <span className={data.webhook_configured ? "text-emerald-700" : "text-amber-700"}>{data.webhook_configured ? "Provider webhooks active — outcomes update automatically." : "Provider webhooks not configured — outcomes stop at 'Accepted'. Add the Resend webhook secret under Branding → Sender."}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {["", ...data.outcomes.filter((o) => data.counts[o] > 0 || ["ACCEPTED", "DELIVERED", "FAILED"].includes(o))].map((o) => (
          <button key={o || "all"} type="button" onClick={() => setFilter(o)} data-testid={`delivery-filter-${o || "all"}`}
            className={`px-2 py-0.5 rounded-full text-[11px] border ${filter === o ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            {o ? o.replace(/_/g, " ") : "All"} {o ? `(${data.counts[o] || 0})` : ""}
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search recipient / subject / customer" className="h-7 w-64 text-[11px] ml-auto" data-testid="delivery-search" />
      </div>
      <div className="border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
        {data.items.length === 0 && <div className="p-4 text-sm text-slate-400">No messages match.</div>}
        {data.items.map((l) => (
          <div key={l.id} className="px-4 py-2 text-[12px]" data-testid={`delivery-row-${l.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <OutcomeBadge outcome={l.outcome} help={l.outcome_help} />
              <button type="button" className="font-medium text-slate-800 hover:underline text-left" onClick={() => api.get(`/comms/delivery/${l.id}`).then(({ data }) => setDetail(data))} data-testid={`delivery-open-${l.id}`}>{l.subject}</button>
              <span className="text-slate-500">→ {l.recipient}</span>
              {l.template && <span className="text-slate-400">· {l.template}</span>}
              <span className="text-slate-400 ml-auto">{(l.sent_at || "").slice(0, 16).replace("T", " ")}{l.attempts > 1 ? ` · ${l.attempts} attempts` : ""}</span>
            </div>
            {(l.error || l.action) && (
              <div className="mt-1 text-[11px]">
                {l.error && <div className="text-rose-700">{l.error}</div>}
                {l.action && <div className="text-slate-600">→ {l.action}</div>}
                {["FAILED", "SUPPRESSED", "BOUNCED"].includes(l.outcome) && <div className="mt-1"><ReasonAction label="Resend (controlled)" onConfirm={(r) => resend(l.id, r)} testid={`delivery-resend-${l.id}`} /></div>}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border border-slate-200 rounded-md bg-white" data-testid="suppressions-panel">
        <div className="px-4 py-2 text-[11px] uppercase text-slate-400 border-b border-slate-100">Suppression list · {sups.length} active — hard bounces, complaints and invalid addresses are never re-sent automatically</div>
        {sups.length === 0 && <div className="p-3 text-[12px] text-slate-400">No suppressed addresses.</div>}
        {sups.map((s) => (
          <div key={s.email} className="px-4 py-2 text-[12px] flex items-center gap-2 flex-wrap border-b border-slate-50" data-testid={`suppression-${s.email}`}>
            <span className="font-medium">{s.email}</span>
            <span className="rounded bg-rose-50 text-rose-700 px-1.5 py-0.5 text-[10px]">{s.reason}</span>
            {s.detail && <span className="text-slate-500">{s.detail}</span>}
            <span className="text-slate-400 ml-auto">{(s.at || "").slice(0, 10)}</span>
            <ReasonAction label="Release" onConfirm={(r) => release(s.email, r)} testid={`suppression-release-${s.email}`} />
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="delivery-detail-dialog">
          <DialogHeader><DialogTitle className="text-base">Message detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-[12px]">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 border border-slate-200 rounded-md p-3 bg-slate-50/60">
                <div><span className="text-slate-500">Outcome:</span> <OutcomeBadge outcome={detail.outcome} help={detail.outcome_help} /> <span className="text-slate-500">{detail.outcome_help}</span></div>
                <div><span className="text-slate-500">To:</span> <span className="font-medium">{detail.recipient}</span>{detail.cc?.length ? <span className="text-slate-500"> · cc {detail.cc.join(", ")}</span> : null}</div>
                <div><span className="text-slate-500">From:</span> <span className="font-medium">{detail.from}</span></div>
                <div><span className="text-slate-500">Reply-To:</span> <span className="font-medium">{detail.reply_to}</span></div>
                <div className="sm:col-span-2"><span className="text-slate-500">Subject:</span> <span className="font-medium">{detail.subject}</span></div>
                {detail.requested_from?.from_email && <div className="sm:col-span-2 text-slate-500">Module requested sender {detail.requested_from.from_email} — overridden to the approved identity.</div>}
                {detail.attachments?.length > 0 && <div className="sm:col-span-2"><span className="text-slate-500">Attachments:</span> {detail.attachments.map((a) => `${a.filename} (${Math.round(a.size_bytes / 1024)} KB)`).join(", ")}</div>}
                {detail.provider_id && <div className="sm:col-span-2 text-slate-400">Provider id {detail.provider_id} · credential {detail.credential_source}</div>}
                {detail.events?.length > 0 && <div className="sm:col-span-2 text-slate-500">Events: {detail.events.map((e) => `${e.type} @ ${(e.received_at || e.at || "").slice(0, 16)}`).join(" · ")}</div>}
              </div>
              <EmailPreviewFrame html={detail.preview?.html} text={detail.preview?.text} testid="delivery-preview" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
